import type {
  Appointment,
  CreateStaffRequest,
  CreateStaffResponse,
  StaffMember,
  AppointmentStatus,
  AvailabilitySlot,
  ClinicianSummary,
  DocumentView,
  JoinResponse,
  Outcome,
  OutcomeRecord,
  RequestUploadRequest,
  Role,
  Specialty,
} from "@so/shared";
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { api } from "./client.js";

export interface Me {
  readonly userId: string;
  readonly email: string;
  readonly name: string;
  readonly roles: readonly Role[];
  readonly timezone: string | null;
  readonly onboarded: boolean;
}

export interface QueueFlags {
  readonly awkward_for_patient: boolean;
  readonly awkward_for_clinician: boolean;
  readonly hours_apart: number;
  readonly starts_within_24h: boolean;
  readonly in_the_past: boolean;
}

export type QueueItem = Appointment & { readonly flags: QueueFlags };

export interface OutcomeResponse {
  readonly appointment_id: string;
  readonly patient_name: string;
  readonly clinician_name: string;
  readonly specialty: string;
  readonly consulted_at: string;
  readonly outcome: OutcomeRecord;
  readonly concurrence_label: string;
  readonly urgency_label: string;
}

/** Query keys in one place, so an invalidation cannot miss a cache. */
export const keys = {
  me: ["me"] as const,
  clinicians: (specialty?: string) => ["clinicians", specialty ?? "all"] as const,
  clinician: (id: string) => ["clinician", id] as const,
  availability: (id: string, includeBooked: boolean) =>
    ["availability", id, includeBooked] as const,
  appointments: ["appointments"] as const,
  appointment: (id: string) => ["appointment", id] as const,
  documents: (id: string) => ["documents", id] as const,
  outcome: (id: string) => ["outcome", id] as const,
  queue: (status: AppointmentStatus) => ["queue", status] as const,
  staff: ["staff"] as const,
};

export function useMe(options?: Partial<UseQueryOptions<Me>>) {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api<Me>("/me"),
    // Roles and timezone change rarely, and every screen depends on this.
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function useUpdateMe() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { name?: string; timezone?: string }) =>
      api<Me>("/me", { method: "PUT", body: input }),
    onSuccess: (me) => {
      client.setQueryData(keys.me, me);
    },
  });
}

export function useClinicians(specialty?: Specialty) {
  return useQuery({
    queryKey: keys.clinicians(specialty),
    queryFn: () =>
      api<{ clinicians: ClinicianSummary[] }>("/clinicians", {
        query: { specialty },
      }).then((r) => r.clinicians),
    staleTime: 5 * 60_000,
  });
}

export function useClinician(id: string) {
  return useQuery({
    queryKey: keys.clinician(id),
    queryFn: () => api<ClinicianSummary>(`/clinicians/${id}`),
    staleTime: 5 * 60_000,
  });
}

export interface AvailabilityResponse {
  readonly clinician_id: string;
  readonly timezone: string;
  readonly slots: readonly Pick<AvailabilitySlot, "starts_at" | "ends_at" | "state">[];
}

export function useAvailability(id: string, includeBooked = false) {
  return useQuery({
    queryKey: keys.availability(id, includeBooked),
    queryFn: () =>
      api<AvailabilityResponse>(`/clinicians/${id}/availability`, {
        query: { include_booked: includeBooked },
      }),
    // Slots are taken by other people; a stale calendar means a booking that
    // fails at the last step.
    staleTime: 15_000,
  });
}

export function useSetAvailability(clinicianId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (slots: { starts_at: string; duration_minutes: number }[]) =>
      api<{ added: string[]; removed: string[]; kept_because_booked: string[] }>(
        `/clinicians/${clinicianId}/availability`,
        { method: "PUT", body: { slots } },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["availability", clinicianId] });
    },
  });
}

export function useAppointments() {
  return useQuery({
    queryKey: keys.appointments,
    queryFn: () =>
      api<{ appointments: Appointment[] }>("/appointments").then((r) => r.appointments),
  });
}

export function useAppointment(id: string) {
  return useQuery({
    queryKey: keys.appointment(id),
    queryFn: () => api<Appointment>(`/appointments/${id}`),
  });
}

export function useBookAppointment() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { clinician_id: string; starts_at: string; reason: string }) =>
      api<Appointment>("/appointments", { method: "POST", body: input }),
    onSuccess: (appointment) => {
      void client.invalidateQueries({ queryKey: keys.appointments });
      void client.invalidateQueries({ queryKey: ["availability", appointment.clinician_id] });
    },
  });
}

/**
 * Every state change on an appointment goes through here.
 *
 * They all invalidate the same things, and doing that in one place is what
 * stops a screen showing a cancelled consultation as still confirmed because
 * somebody forgot an invalidation on one of six mutations.
 */
function useAppointmentAction<TBody>(action: string, method: "PUT" = "PUT") {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body?: TBody }) =>
      api<Appointment>(`/appointments/${id}/${action}`, { method, body: body ?? {} }),
    onSuccess: (appointment) => {
      client.setQueryData(keys.appointment(appointment.appointment_id), appointment);
      void client.invalidateQueries({ queryKey: keys.appointments });
      void client.invalidateQueries({ queryKey: ["queue"] });
      void client.invalidateQueries({ queryKey: ["availability", appointment.clinician_id] });
    },
  });
}

export const useAcceptAppointment = () => useAppointmentAction<never>("accept");
export const useCancelAppointment = () => useAppointmentAction<{ reason?: string }>("cancel");
export const useRescheduleAppointment = () =>
  useAppointmentAction<{ starts_at: string; reason?: string }>("reschedule");
export const useReinstateAppointment = () =>
  useAppointmentAction<{ starts_at: string }>("reinstate");

export interface DocumentsResponse {
  readonly appointment_id: string;
  readonly documents: readonly DocumentView[];
  readonly pending: number;
}

export function useDocuments(appointmentId: string) {
  return useQuery({
    queryKey: keys.documents(appointmentId),
    queryFn: () => api<DocumentsResponse>(`/appointments/${appointmentId}/documents`),
    // Download links are presigned for five minutes, so a page left open
    // would otherwise hand out links that have already expired.
    staleTime: 60_000,
    refetchInterval: 4 * 60_000,
  });
}

export interface UploadTicket {
  readonly document_id: string;
  readonly upload_url: string;
  readonly required_headers: Record<string, string>;
}

export function useRequestUpload(appointmentId: string) {
  return useMutation({
    mutationFn: (input: RequestUploadRequest) =>
      api<UploadTicket>(`/appointments/${appointmentId}/documents`, {
        method: "POST",
        body: input,
      }),
  });
}

export function useJoinMeeting(appointmentId: string) {
  return useMutation({
    mutationFn: () =>
      api<JoinResponse>(`/appointments/${appointmentId}/meeting`, { method: "POST" }),
  });
}

export function useEndMeeting(appointmentId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () =>
      api<Appointment>(`/appointments/${appointmentId}/meeting`, { method: "DELETE" }),
    onSuccess: (appointment) => {
      client.setQueryData(keys.appointment(appointmentId), appointment);
      void client.invalidateQueries({ queryKey: keys.appointments });
    },
  });
}

export function useOutcome(appointmentId: string, enabled = true) {
  return useQuery({
    queryKey: keys.outcome(appointmentId),
    queryFn: () => api<OutcomeResponse>(`/appointments/${appointmentId}/outcome`),
    enabled,
    // A consultation with no opinion yet answers 404, which is the expected
    // state rather than a failure - retrying would just repeat it.
    retry: false,
  });
}

export function useSubmitOutcome(appointmentId: string) {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (outcome: Outcome) =>
      api<{ status: AppointmentStatus; outcome: OutcomeRecord }>(
        `/appointments/${appointmentId}/outcome`,
        { method: "PUT", body: outcome },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.appointment(appointmentId) });
      void client.invalidateQueries({ queryKey: keys.outcome(appointmentId) });
      void client.invalidateQueries({ queryKey: keys.appointments });
    },
  });
}

export interface QueueResponse {
  readonly status: AppointmentStatus;
  readonly count: number;
  readonly appointments: readonly QueueItem[];
  readonly needs_attention: number;
}

export function useQueue(status: AppointmentStatus) {
  return useQuery({
    queryKey: keys.queue(status),
    queryFn: () => api<QueueResponse>("/admin/queue", { query: { status } }),
    // A coordinator watches this screen while working.
    refetchInterval: 30_000,
  });
}

export interface StaffResponse {
  readonly staff: readonly StaffMember[];
  readonly count: number;
}

export function useStaff() {
  return useQuery({
    queryKey: keys.staff,
    queryFn: () => api<StaffResponse>("/admin/staff"),
    staleTime: 30_000,
  });
}

export function useCreateStaff() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateStaffRequest) =>
      api<CreateStaffResponse>("/admin/staff", { method: "POST", body: input }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.staff });
      // A new specialist appears in the patient-facing browse list too.
      void client.invalidateQueries({ queryKey: ["clinicians"] });
    },
  });
}

export function useSetStaffEnabled() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, enabled, reason }: { userId: string; enabled: boolean; reason?: string }) =>
      api<{ userId: string; enabled: boolean }>(`/admin/staff/${userId}`, {
        method: "PUT",
        body: { enabled, ...(reason === undefined ? {} : { reason }) },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.staff });
      void client.invalidateQueries({ queryKey: ["clinicians"] });
    },
  });
}
